import type { HTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';

export type BadgeTone =
  'primary' | 'cyan' | 'dark-blue' | 'orange' | 'success' | 'warning' | 'danger' | 'neutral';

/**
 * Icoon- en tekstkleur komen altijd uit dezelfde kleurfamilie als de achtergrond
 * (style guide §Iconografie — tinting rules). De -aa-varianten houden het contrast
 * op tint-achtergronden boven 4,5:1.
 */
const toneClasses: Record<BadgeTone, string> = {
  primary: 'bg-brand-primary-tint text-[var(--primary-tint-text-aa)]',
  cyan: 'bg-brand-cyan-tint text-[#0891A1]',
  'dark-blue': 'bg-brand-dark-blue-tint text-brand-dark-blue',
  orange: 'bg-brand-orange-tint text-[var(--orange-tint-text-aa)]',
  success: 'bg-success-tint text-[var(--success-aa)]',
  warning: 'bg-warning-tint text-[var(--warning-aa)]',
  danger: 'bg-danger-tint text-danger',
  neutral: 'bg-border-light text-text-secondary',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
}

/** Context-chip / type-badge (style guide §Badges & tags). */
export function Badge({ className, tone = 'neutral', ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-pill px-2.5 py-1 font-heading text-support font-semibold uppercase tracking-[0.08em]',
        toneClasses[tone],
        className,
      )}
      {...props}
    />
  );
}

/** Numerieke indicator naast een label of navigatie-item. */
export function CountPill({ value, className }: { value: number; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex min-w-[20px] items-center justify-center rounded-pill bg-brand-orange px-1.5 py-0.5 font-heading text-tab font-semibold text-text-on-dark',
        className,
      )}
    >
      {value > 99 ? '99+' : value}
    </span>
  );
}

/** Statusstip met toegankelijk label (nooit kleur alleen als betekenisdrager). */
export function StatusDot({ tone, label }: { tone: BadgeTone; label: string }) {
  const dotColor: Record<BadgeTone, string> = {
    primary: 'bg-brand-primary',
    cyan: 'bg-brand-cyan',
    'dark-blue': 'bg-brand-dark-blue',
    orange: 'bg-brand-orange',
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-danger',
    neutral: 'bg-text-muted',
  };
  return (
    <span className="inline-flex items-center gap-inline text-small text-text-secondary">
      <span aria-hidden className={cn('h-2 w-2 rounded-pill', dotColor[tone])} />
      {label}
    </span>
  );
}
